import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search, User, Users, Clock, Filter } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";

interface Employee {
  user_id: string;
  full_name: string;
  first_name: string;
  last_name: string;
  email: string;
  mobile_phone: string;
  avatar_url: string;
  team_id: string;
  team_name: string;
  is_available: boolean;
  role?: string; // Optional since not returned by the RPC
}

interface Team {
  id: string;
  name: string;
}

interface EmployeeSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  sessionStart: string;
  sessionEnd: string;
  onEmployeesSelected: (employees: string[]) => void;
  selectedEmployees: string[];
}

export const EmployeeSelector = ({
  isOpen,
  onClose,
  sessionStart,
  sessionEnd,
  onEmployeesSelected,
  selectedEmployees
}: EmployeeSelectorProps) => {
  const { language } = useLanguage();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [localSelectedEmployees, setLocalSelectedEmployees] = useState<string[]>(selectedEmployees);

  useEffect(() => {
    if (isOpen && sessionStart && sessionEnd) {
      fetchAvailableEmployees();
    }
  }, [isOpen, sessionStart, sessionEnd]);

  useEffect(() => {
    if (employees.length > 0) {
      fetchTeams();
    }
  }, [employees]);

  useEffect(() => {
    setLocalSelectedEmployees(selectedEmployees);
  }, [selectedEmployees]);

  const fetchAvailableEmployees = async () => {
    try {
      setLoading(true);
      
      console.log('Fetching employees with:', { sessionStart, sessionEnd });
      
      const { data, error } = await supabase.rpc('get_available_employees_for_session', {
        session_start: sessionStart,
        session_end: sessionEnd
      });

      console.log('Function response:', { data, error });

      if (error) throw error;
      
      // Remove duplicates by user_id and combine team names
      const uniqueEmployees = (data as any || []).reduce((acc: Employee[], employee: any) => {
        const existingEmployee = acc.find(emp => emp.user_id === employee.user_id);
        if (existingEmployee) {
          // Combine team names if different
          if (existingEmployee.team_name !== employee.team_name) {
            existingEmployee.team_name = `${existingEmployee.team_name}, ${employee.team_name}`;
          }
        } else {
          acc.push(employee);
        }
        return acc;
      }, []);
      
      setEmployees(uniqueEmployees as any);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error(language === 'en' ? 'Failed to load employees' : 'Αποτυχία φόρτωσης υπαλλήλων');
    } finally {
      setLoading(false);
    }
};

  const fetchTeams = async () => {
    try {
      const { data, error } = await supabase
        .from('teams')
        .select('id, name')
        .order('name');

      if (error) throw error;
      
      // Get unique teams from employees if teams fetch fails
      const uniqueTeams = [...new Set(employees.map(emp => emp.team_name).filter(Boolean))];
      const teamsFromEmployees = uniqueTeams.map((name, index) => ({
        id: `team-${index}`,
        name: name as string
      }));
      
      setTeams(data && data.length > 0 ? data : teamsFromEmployees);
    } catch (error) {
      console.error('Error fetching teams:', error);
      // Extract unique teams from current employees
      const uniqueTeams = [...new Set(employees.map(emp => emp.team_name).filter(Boolean))];
      const teamsFromEmployees = uniqueTeams.map((name, index) => ({
        id: `team-${index}`,
        name: name as string
      }));
      setTeams(teamsFromEmployees);
    }
  };

  const filteredEmployees = employees.filter(employee => {
    // Text search filter
    const matchesSearch = employee.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.team_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Team filter
    const matchesTeam = selectedTeams.length === 0 || 
      selectedTeams.some(teamName => 
        employee.team_name?.toLowerCase().includes(teamName.toLowerCase())
      );
    
    return matchesSearch && matchesTeam;
  });

  const availableEmployees = filteredEmployees.filter(emp => emp.is_available);
  const unavailableEmployees = filteredEmployees.filter(emp => !emp.is_available);

  const handleEmployeeToggle = (employeeId: string) => {
    setLocalSelectedEmployees(prev => 
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleTeamToggle = (teamName: string) => {
    setSelectedTeams(prev => 
      prev.includes(teamName)
        ? prev.filter(name => name !== teamName)
        : [...prev, teamName]
    );
  };

  const handleSelectAllTeams = () => {
    const allTeamNames = teams.map(team => team.name);
    setSelectedTeams(allTeamNames);
  };

  const handleClearAllTeams = () => {
    setSelectedTeams([]);
  };

  const handleSelectAll = () => {
    const availableIds = availableEmployees.map(emp => emp.user_id);
    setLocalSelectedEmployees(availableIds);
  };

  const handleClearAll = () => {
    setLocalSelectedEmployees([]);
  };

  const handleConfirm = () => {
    onEmployeesSelected(localSelectedEmployees);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Users className="h-5 w-5 mr-2" />
            {language === 'en' ? 'Select Employees' : 'Επιλογή Υπαλλήλων'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search and Controls */}
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={language === 'en' ? 'Search employees...' : 'Αναζήτηση υπαλλήλων...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleSelectAll}>
                {language === 'en' ? 'Select All Available' : 'Επιλογή Όλων Διαθέσιμων'}
              </Button>
              <Button variant="outline" size="sm" onClick={handleClearAll}>
                {language === 'en' ? 'Clear All' : 'Καθαρισμός Όλων'}
              </Button>
            </div>
          </div>

          {/* Team Filters */}
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium text-sm">
                  {language === 'en' ? 'Team Filters' : 'Φίλτρα Ομάδων'}
                </span>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={handleSelectAllTeams}>
                  {language === 'en' ? 'All' : 'Όλες'}
                </Button>
                <Button variant="ghost" size="sm" onClick={handleClearAllTeams}>
                  {language === 'en' ? 'None' : 'Καμία'}
                </Button>
              </div>
            </div>
            
            {teams.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {teams.map((team) => (
                  <div key={team.id} className="flex items-center space-x-2">
                    <Checkbox
                      checked={selectedTeams.includes(team.name)}
                      onCheckedChange={() => handleTeamToggle(team.name)}
                    />
                    <span className="text-sm">{team.name}</span>
                  </div>
                ))}
              </div>
            )}
            
            {selectedTeams.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {selectedTeams.map((teamName) => (
                  <Badge key={teamName} variant="secondary" className="text-xs">
                    {teamName}
                    <button
                      onClick={() => handleTeamToggle(teamName)}
                      className="ml-1 text-muted-foreground hover:text-foreground"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Statistics */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center">
              <User className="h-4 w-4 mr-1" />
              {language === 'en' ? 'Available:' : 'Διαθέσιμοι:'} {availableEmployees.length}
            </span>
            <span className="flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              {language === 'en' ? 'Busy:' : 'Απασχολημένοι:'} {unavailableEmployees.length}
            </span>
            <span className="flex items-center">
              <Users className="h-4 w-4 mr-1" />
              {language === 'en' ? 'Selected:' : 'Επιλεγμένοι:'} {localSelectedEmployees.length}
            </span>
          </div>

          {/* Employee List */}
          <div className="overflow-y-auto max-h-96 space-y-2">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <>
                {/* Available Employees */}
                {availableEmployees.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="font-medium text-sm text-green-600">
                      {language === 'en' ? 'Available Employees' : 'Διαθέσιμοι Υπάλληλοι'}
                    </h3>
                    {availableEmployees.map((employee) => (
                      <div key={employee.user_id} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50">
                        <Checkbox
                          checked={localSelectedEmployees.includes(employee.user_id)}
                          onCheckedChange={() => handleEmployeeToggle(employee.user_id)}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{employee.full_name}</span>
                            <Badge variant="outline" className="text-green-600 border-green-600">
                              {language === 'en' ? 'Available' : 'Διαθέσιμος'}
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {employee.email} • {employee.team_name || 'No Team'}{employee.role ? ` • ${employee.role}` : ''}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Unavailable Employees */}
                {unavailableEmployees.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="font-medium text-sm text-red-600">
                      {language === 'en' ? 'Busy Employees' : 'Απασχολημένοι Υπάλληλοι'}
                    </h3>
                    {unavailableEmployees.map((employee) => (
                      <div key={employee.user_id} className="flex items-center space-x-3 p-3 border rounded-lg bg-muted/30 opacity-60">
                        <Checkbox disabled />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{employee.full_name}</span>
                            <Badge variant="outline" className="text-red-600 border-red-600">
                              {language === 'en' ? 'Busy' : 'Απασχολημένος'}
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {employee.email} • {employee.team_name || 'No Team'}{employee.role ? ` • ${employee.role}` : ''}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {filteredEmployees.length === 0 && !loading && (
                  <div className="text-center py-8 text-muted-foreground">
                    {language === 'en' ? 'No employees found' : 'Δεν βρέθηκαν υπάλληλοι'}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              {language === 'en' ? 'Cancel' : 'Ακύρωση'}
            </Button>
            <Button onClick={handleConfirm}>
              {language === 'en' 
                ? `Confirm (${localSelectedEmployees.length} selected)` 
                : `Επιβεβαίωση (${localSelectedEmployees.length} επιλεγμένοι)`
              }
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};